import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiLoginStep, apiVerify2fa, apiResendVerification, apiErrorCode, apiGetPublicConfig } from '../api';
import PasswordInput from '../components/PasswordInput';
import BandzLogo from '../components/BandzLogo';

export default function LoginPage() {
  const { setUser } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [username, setUsername] = useState('demo');
  const [password, setPassword] = useState('demo');
  const [code, setCode] = useState('');
  const [pendingToken, setPendingToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Set when the server refuses the login with 403 `email_not_verified`: the
  // account exists but its e-mail was never confirmed (L3 self-service signup).
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  // Self-service registration state (server-authoritative, see
  // server/lib/features.js). Defaults to OPEN so a slow/failed lookup never
  // hides a working link; the server refuses with 403 if it is really closed.
  const [signupOpen, setSignupOpen] = useState(true);

  useEffect(() => {
    let alive = true;
    apiGetPublicConfig().then((c) => { if (alive) setSignupOpen(c.signupEnabled); });
    return () => { alive = false; };
  }, []);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setNeedsVerification(false);
    setResent(false);
    setBusy(true);
    try {
      if (pendingToken) {
        // Step 2: exchange pending token + TOTP code for a real session.
        const user = await apiVerify2fa(pendingToken, code);
        setUser(user);
        window.dispatchEvent(new Event('bandz-auth-login'));
        navigate('/home', { replace: true });
      } else {
        // Step 1: password. 2FA accounts return a pending token instead.
        const res = await apiLoginStep(username, password);
        if (res.twoFactorRequired && res.pendingToken) {
          setPendingToken(res.pendingToken);
        } else if (res.user) {
          setUser(res.user);
          window.dispatchEvent(new Event('bandz-auth-login'));
          navigate('/home', { replace: true });
        } else {
          setError(t('login.unexpectedResponse'));
        }
      }
    } catch (err: any) {
      const msg = err?.message || '';
      const errCode = apiErrorCode(err);
      if (errCode === 'email_not_verified') {
        // Clear message + a way out: resend the verification link.
        setNeedsVerification(true);
        setResendEmail(username.includes('@') ? username : '');
        setError(t('login.emailNotVerified'));
      } else {
        setError(
          errCode === 'too_many_requests' || msg.includes('429') ? t('login.tooManyAttempts')
          : msg.includes('401') ? (pendingToken ? t('login.invalidCode') : t('login.invalidCredentials'))
          : msg.includes('403') ? t('login.deactivated')
          : t('login.loginFailed'),
        );
      }
    } finally {
      setBusy(false);
    }
  }

  async function resendVerification() {
    const email = resendEmail.trim();
    if (!email) { setError(t('login.resendEmailRequired')); return; }
    setResending(true);
    setError(null);
    try {
      await apiResendVerification(email);
      setResent(true);
    } catch (err) {
      setError(apiErrorCode(err) === 'too_many_requests' ? t('login.tooManyAttempts') : t('login.resendFailed'));
    } finally {
      setResending(false);
    }
  }

  return (
    <div className="login-page" data-page="login">
      <div className="login-wrap">
        <BandzLogo className="login-logo" />
        <div className="login-card">
        <h1>{t('login.title')}</h1>
        <p className="login-subtitle">
          {pendingToken ? t('login.enter2fa') : t('login.signInToContinue')}
        </p>
        <form onSubmit={onSubmit}>
          {!pendingToken ? (
            <>
              <label>
                {t('login.username')}
                <input
                  type="text"
                  autoFocus
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="username"
                />
              </label>
              <label>
                {t('login.password')}
                <PasswordInput
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                />
              </label>
            </>
          ) : (
            <>
              <label>
                {t('login.authCode')}
                <input
                  type="text"
                  autoFocus
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder={t('login.codePh')}
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
              </label>
              <button
                type="button"
                className="login-btn login-btn-ghost"
                onClick={() => { setPendingToken(null); setCode(''); setError(null); }}
                disabled={busy}
              >
                {t('login.back')}
              </button>
            </>
          )}
          {error && <div className="login-error">{error}</div>}
          {needsVerification && (
            <div className="login-resend">
              {resent && <div className="signup-ok">{t('login.verificationSent')}</div>}
              <label>
                {t('login.resendEmailLabel')}
                <input
                  type="email"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  autoComplete="email"
                />
              </label>
              <button
                type="button"
                className="login-btn login-btn-ghost"
                onClick={resendVerification}
                disabled={resending}
              >
                {resending ? t('login.resending') : t('login.resendVerification')}
              </button>
            </div>
          )}
          <button type="submit" className="login-btn" disabled={busy}>
            {busy ? t('login.signingIn') : pendingToken ? t('login.verify') : t('login.signIn')}
          </button>
        </form>
        <p className="signup-alt">
          {signupOpen ? (
            <>{t('login.noAccount')} <Link to="/signup">{t('login.createAccount')}</Link></>
          ) : (
            <>{t('login.signupClosed')}</>
          )}
        </p>
        </div>
        <p className="login-demo-note">{t('login.demoReadOnly')}</p>
      </div>
    </div>
  );
}